import { useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useState } from 'react';

const validateZipCode = (value: string) => {
  // example: 94105-2501 or 94016
  const zipCodePattern = /^[0-9]{5}(?:-[0-9]{4})?$/;
  return zipCodePattern.test(value);
};

const validatePhoneNumber = (value: string) => {
  // example: 123-456-7890
  const phoneNumberPattern =
    /^\(?([0-9]{3})\)?[-.●]?([0-9]{3})[-.●]?([0-9]{4})$/;
  return phoneNumberPattern.test(value);
};

export const FIELD_TYPES = {
  ZIP_CODE: 'zipCode',
  PHONE_NUMBER: 'phoneNumber',
};

interface UseInputButtonProps {
  fieldType: string;
  appendQueryParamToUrl: boolean;
  paramName: string;
}

export const useInputButton = ({
  fieldType,
  appendQueryParamToUrl,
  paramName,
}: UseInputButtonProps) => {
  const searchParams = useSearchParams();
  const [inputValue, setInputValue] = useState('');
  const [error, setError] = useState('');

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setInputValue(value);

    let isValid = false;

    const validators: { [key: string]: (value: string) => boolean } = {
      [FIELD_TYPES.ZIP_CODE]: validateZipCode,
      [FIELD_TYPES.PHONE_NUMBER]: validatePhoneNumber,
    };

    isValid = validators[fieldType]?.(value) ?? false;
    setError(isValid ? '' : `Invalid ${fieldType}`);
  };

  const updateUrlWithInputValueParam = useCallback(() => {
    const url = new URL(window.location.href);
    if (url.searchParams.has(paramName)) {
      url.searchParams.set(paramName, inputValue);
    } else {
      url.searchParams.append(paramName, inputValue);
    }
    window.history.replaceState({}, '', url.toString());
  }, [paramName, inputValue]);

  const removeSearchParam = useCallback(() => {
    const url = new URL(window.location.href);

    if (url.searchParams.has(paramName)) {
      url.searchParams.delete(paramName);
      window.history.replaceState({}, '', url.toString());
    }
  }, [paramName]);

  useEffect(() => {
    // If the query param is already in the URL, set the input value to the query param value
    const paramNameValue = searchParams.get(paramName);
    if (appendQueryParamToUrl && paramName && paramNameValue && !inputValue) {
      setInputValue(paramNameValue);
    }
  }, [appendQueryParamToUrl, paramName, inputValue, searchParams]);

  useEffect(() => {
    if (error) {
      removeSearchParam();
      return;
    }

    if (appendQueryParamToUrl && paramName && inputValue && !error) {
      updateUrlWithInputValueParam();
    }
  }, [
    appendQueryParamToUrl,
    paramName,
    updateUrlWithInputValueParam,
    inputValue,
    error,
    removeSearchParam,
  ]);

  return {
    inputValue,
    error,
    handleChange,
  };
};
